import React from 'react';
import { Box, Stack } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import StarIcon from '@mui/icons-material/Star';
import { Bookmark, BookmarkPage, Comments, EmergencyAlerts, Pages, VisibilityOff } from '../assets/icons';
import PropTypes from 'prop-types';
import ThumbnailPage from './thumbnailItem';
import moment from 'moment';

const useStyles = makeStyles((theme) => ({
    root: {
        '--pages-icon-color': theme.palette.documentViewer.buttonsBackgroundColor,
        padding: '8px',
        backgroundColor: theme.palette.documentViewer.timelineItemBackgroundColor,
        borderRadius: '6px',
        borderLeftWidth: '3px',
        borderLeftStyle: 'solid',
        borderLeftColor: theme.palette.documentViewer.timelineItemBackgroundColor,
        cursor: 'default',
        '& .icons': {
            color: theme.palette.documentViewer.lowContrastTextColor,
        },
        '&.opened': {
            color: theme.palette.documentViewer.lowContrastText2Color,
            backgroundColor: theme.palette.documentViewer.lowContrastText4Color,
            borderLeftColor: theme.palette.documentViewer.lowContrastText4Color,
            '&not:(.selected) .icons': {
                color: theme.palette.documentViewer.lowContrastText2Color,
            },
            '&.selected .icons': {
                color: theme.palette.default.contrastBackground,
            },
        },
        '&.selected': {
            color: theme.palette.default.contrastBackground,
            backgroundColor: theme.palette.default.main,
            borderLeftColor: theme.palette.default.main,
        },
        '&.pointer': {
            cursor: 'pointer',
        },
    },
    thumbnailContainer: {
        position: 'relative',
        width: '48px',
        height: '56px',
        '&.selected': {
            width: '68px',
            height: '82px',
        },
    },
    backPage: {
        position: 'absolute',
        top: 0,
        right: 0,
        width: '40px',
        height: '52px',
        backgroundColor: theme.palette.documentViewer.thumbnailBackPageBackgroundColor,
        borderWidth: '0.75px',
        borderStyle: 'solid',
        borderColor: theme.palette.documentViewer.thumbnailBackPageBorderColor,
        borderRadius: '5px',
        '&.selected': {
            width: '60px',
            height: '78px',
        },
    },
    backPage3: {
        top: 0,
        right: 0,
        zIndex: 1,
    },
    backPage2: {
        top: '2px',
        right: '2px',

        zIndex: 2,
    },
    backPage1: {
        top: '4px',
        right: '4px',
        zIndex: 3,
        paddingTop: '1px',
        paddingLeft: '1px',
    },
    frontPageNumberIco: {
        color: theme.palette.text.primary,
        position: 'absolute',
        left: '16px',
        top: '18px',
        zIndex: 4,
        '&.selected': {
            left: '25px',
            top: '27px',
        },
    },
    frontPageNumber: {
        position: 'absolute',
        left: '12px',
        top: '21px',
        width: '24px',
        lineHeight: '19px',
        textAlign: 'center',
        fontFamily: 'Roboto',
        fontSize: '11px',
        fontStyle: 'normal',
        fontWeight: 900,
        zIndex: 5,
        cursor: 'default',
        color: theme.palette.text.primary,
        '&.selected': {
            left: '21px',
            top: '30px',
        },
    },
    textSection: {
        maxWidth: 'calc(100% - 54px)',
        width: 'calc(100% - 54px)',
        '&.selected': {
            maxWidth: 'calc(100% - 74px)',
            width: 'calc(100% - 74px)',
        },
    },
    fileName: {
        textAlign: 'left',
        fontFamily: 'Inter',
        fontSize: '12px',
        fontStyle: 'normal',
        fontWeight: 500,
        lineHeight: 'normal',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    docDate: {
        fontFamily: 'Inter',
        fontSize: '8px',
        fontStyle: 'normal',
        fontWeight: 500,
        lineHeight: 'normal',
    },
    iconsContainer: {
        fontSize: '13px',
        '& .MuiSvgIcon-root': {
            fontSize: '16px',
        },
        cursor: 'default',
    },
    rightSideIcons: {
        position: 'relative',
    },
    bookmarkIcon: {
        top: '-9px',
        position: 'absolute',
        right: '2px',
    },
    specialty: {
        //temporary , not important , only for figma view
        fontFamily: 'Inter',
        fontSize: '9px',
        fontStyle: 'normal',
        fontWeight: '500',
        lineHeight: 'normal',
        padding: '5px 9px',
        borderRadius: '5px',
        '&.pink': {
            backgroundColor: '#C79BFF', //temporary , not important , only for figma view
            color: '#6401B1',
        },
        '&.lemon': {
            backgroundColor: '#9BF3FF', //temporary , not important , only for figma view
            color: '#0191B1',
        },
        '&.grey': {
            backgroundColor: '#EFF1F3', //temporary , not important , only for figma view
            color: '#0A0A0A',
        },
    },
}));

export default function TimelineItem(props) {
    const classes = useStyles();

    const [thumbnailWidthHeight, setThumbnailWidthHeight] = React.useState({ w: 37, h: 48 });

    React.useEffect(() => {
        if (props.selected) {
            setThumbnailWidthHeight({ w: 55, h: 74 });
        } else {
            setThumbnailWidthHeight({ w: 37, h: 49 });
        }
    }, [props.selected]);

    const selectIt = () => {
        if (props.onSelect) {
            props.onSelect(props.docId);
        }
    };

    return (
        <Box
            className={`${classes.root} ${props.opened ? 'opened' : ''} ${props.selected ? 'selected' : ''} ${props.onSelect ? 'pointer' : ''}`}
            onClick={() => selectIt()}
            style={props.colorMark ? { borderLeftColor: props.colorMark } : {}}
        >
            <Stack direction={'row'} justifyContent={'space-between'}>
                <Stack direction={'row'} alignItems={'stretch'} gap={'6px'} justifyContent={'flex-start'} maxWidth={'calc(100%  - 22px)'}>
                    <Box className={`${classes.thumbnailContainer} ${props.opened ? 'opened' : ''} ${props.selected ? 'selected' : ''}`}>
                        {props.pageCount > 10 && (
                            <Box className={`${classes.backPage} ${classes.backPage3} ${props.selected ? 'selected' : ''}`}></Box>
                        )}
                        {props.pageCount > 1 && (
                            <Box className={`${classes.backPage} ${classes.backPage2} ${props.selected ? 'selected' : ''}`}></Box>
                        )}
                        <Box className={`${classes.backPage} ${classes.backPage1} ${props.selected ? 'selected' : ''}`}>
                            <ThumbnailPage
                                docId={props.docId}
                                width={thumbnailWidthHeight.w}
                                height={thumbnailWidthHeight.h}
                                noStyles={true}
                            />
                        </Box>
                        <Box className={`${classes.frontPageNumberIco} ${props.selected ? 'selected' : ''}`}>
                            <Pages color={'var(--pages-icon-color)'} />
                        </Box>
                        <Box className={`${classes.frontPageNumber} ${props.selected ? 'selected' : ''}`}>{props.pageCount}</Box>
                    </Box>
                    <Stack
                        direction={'column'}
                        justifyContent={'space-between'}
                        className={`${classes.textSection} ${props.selected ? 'selected' : ''}`}
                    >
                        <Box>
                            <Box className={classes.fileName}>{props.fileName}</Box>
                            <Box className={classes.docDate}>
                                {moment.isMoment(props.fileDate) ? props.fileDate.format('DD/MM/YYYY, HH:mm A') : props.fileDate}
                            </Box>
                        </Box>
                        <Stack direction={'row'} gap={'6px'} className={`${classes.iconsContainer} icons`} alignItems={'center'}>
                            {props.hasAlerts && <EmergencyAlerts />}
                            {props.comments > 0 && (
                                <Stack direction={'row'} alignItems={'center'} gap={'1px'}>
                                    <Comments />
                                    <Box>{props.comments}</Box>
                                </Stack>
                            )}
                            {props.bookmarks > 0 && (
                                <Stack direction={'row'} alignItems={'center'} gap={'1px'}>
                                    <BookmarkPage />
                                    <Box>{props.bookmarks}</Box>
                                </Stack>
                            )}
                            {props.hiddenPages > 0 && (
                                <Stack direction={'row'} alignItems={'center'} gap={'1px'}>
                                    <VisibilityOff />
                                    <Box>{props.hiddenPages}</Box>
                                </Stack>
                            )}
                        </Stack>
                        {props.selected && (
                            <Stack direction={'row'} gap={'3px'}>
                                {/*<Box className={`${classes.specialty} pink`}>Psychology</Box>*/}
                                {/*<Box className={`${classes.specialty} lemon`}>Neurology</Box>*/}
                                {/*<Box className={`${classes.specialty} grey`}>...</Box>*/}
                            </Stack>
                        )}
                    </Stack>
                </Stack>
                <Box className={`${classes.rightSideIcons}`}>
                    {props.hasBookmark && (
                        <Box className={`${classes.bookmarkIcon}`}>
                            <Bookmark />
                        </Box>
                    )}

                    {props.isFavorite && <StarIcon style={{ color: '#8569F2' }} />}
                    {/*{!isFavorite && <StarOutlineIcon />}*/}
                </Box>
            </Stack>
        </Box>
    );
}

TimelineItem.defaultProps = {
    pageCount: 0,
    fileName: '-',
    fileDate: '-',
    selected: false,
    hasBookmark: false,
    isFavorite: false,
    opened: false,
    hasAlerts: false,
    comments: 0,
    bookmarks: 0,
    hiddenPages: 0,
};

TimelineItem.propTypes = {
    docId: PropTypes.string.isRequired,
    pageCount: PropTypes.number,
    fileName: PropTypes.string,
    fileDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(moment.Moment)]),
    onSelect: PropTypes.func,
    selected: PropTypes.bool,
    hasBookmark: PropTypes.bool,
    isFavorite: PropTypes.bool,
    opened: PropTypes.bool,
    hasAlerts: PropTypes.bool,
    comments: PropTypes.number,
    bookmarks: PropTypes.number,
    hiddenPages: PropTypes.number,
    colorMark: PropTypes.string,
};
